/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import client from "./client";

/**
 * Organisation-level admin. This is the customer administering their own org —
 * not the Vecros staff console. Every endpoint re-checks the caller's admin
 * role server-side, so hiding the UI is convenience, not the security boundary.
 */
export const whoami = async () => {
  const { data } = await client.get("/drive/admin/me");
  return {
    isOrgAdmin: Boolean(data.is_org_admin),
    organisationId: data.organisation_id ?? null,
    role: data.role ?? null,
  };
};

export const getDashboard = async () => {
  const { data } = await client.get("/drive/admin/dashboard");
  return data;
};

export const listMembers = async () => {
  const { data } = await client.get("/drive/admin/members");
  return data.members || [];
};

export const setMemberRole = async (userId, role) => {
  const { data } = await client.patch(
    `/drive/admin/members/${userId}/role`,
    null,
    { params: { role } }
  );
  return data;
};

export const getMember = async (userId) => {
  const { data } = await client.get(`/drive/admin/members/${userId}`);
  return data.member;
};

/** Every org team, each flagged with whether this member already belongs to it. */
export const getMemberTeams = async (userId) => {
  const { data } = await client.get(`/drive/admin/members/${userId}/teams`);
  return data.teams || [];
};

export const addMemberToTeam = async (userId, teamId, role = "member") => {
  const { data } = await client.post(
    `/drive/admin/members/${userId}/teams/${teamId}`,
    null,
    { params: { role } }
  );
  return data.member;
};

export const removeMemberFromTeam = async (userId, teamId) => {
  const { data } = await client.delete(`/drive/admin/members/${userId}/teams/${teamId}`);
  return data.member;
};

/** Removes someone from the org entirely. Their files are untouched. */
export const removeMember = async (userId) => {
  const { data } = await client.delete(`/drive/admin/members/${userId}`);
  return data;
};

export const listTeams = async () => {
  const { data } = await client.get("/drive/admin/teams");
  return data.teams || [];
};

export const deleteTeam = async (teamId) => {
  const { data } = await client.delete(`/drive/admin/teams/${teamId}`);
  return data.team;
};

/** Manage any team's membership from the admin console, not just teams the admin is in. */
export const listTeamMembers = async (teamId) => {
  const { data } = await client.get(`/drive/admin/teams/${teamId}/members`);
  return data.members || [];
};

export const lookupTeamMember = async (teamId, email) => {
  const { data } = await client.get(`/drive/admin/teams/${teamId}/members/lookup`, { params: { email } });
  return data.user;
};

export const addTeamMember = async (teamId, userId, role = "member") => {
  const { data } = await client.post(`/drive/admin/teams/${teamId}/members`, null, {
    params: { member_user_id: userId, role },
  });
  return data.member;
};

export const removeTeamMember = async (teamId, userId) => {
  const { data } = await client.delete(`/drive/admin/teams/${teamId}/members/${userId}`);
  return data.member;
};

export const setTeamMemberRole = async (teamId, userId, role) => {
  const { data } = await client.patch(`/drive/admin/teams/${teamId}/members/${userId}/role`, null, {
    params: { role },
  });
  return data.member;
};

/**
 * Org-wide trash: every deleted file owned by a member of this org, not just
 * the caller's own. Same restore/permanent-delete semantics as the per-user
 * trash — deleting never touches S3, only permanent delete does.
 */
export const listOrgTrash = async () => {
  const { data } = await client.get("/drive/admin/trash");
  return data.files || [];
};

export const restoreOrgFile = async (fileId) => {
  const { data } = await client.post(`/drive/admin/trash/${fileId}/restore`);
  return data.file;
};

export const deleteOrgFileForever = async (fileId) => {
  const { data } = await client.delete(`/drive/admin/trash/${fileId}`);
  return data.file;
};

/**
 * Recent changes across the org's drive resources, derived from existing
 * created_at/updated_at columns rather than a dedicated event log. Cannot
 * show previews/downloads — only row mutations.
 */
export const getActivity = async ({ entity, limit } = {}) => {
  const { data } = await client.get("/drive/admin/activity", { params: { entity, limit } });
  return data.activity || [];
};
